import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, Link2, Mail, Search, Send, UserPlus, X,
} from 'lucide-react';

import {
  inviteMobileUser, linkMobileAccountPatient, loadMobileAccounts, resendMobileInvitation,
} from './api';
import { Pill, WorkspaceTabs } from './parts';
import { tx } from './i18n';
import { useXomicsAuth } from './AuthContext';
import { errorMessage } from './ui';
import { initials } from './MobileParts';

// Links an app account to the patient whose record is open: an account found in
// the app's directory, or a new one made by inviting the patient, which the
// backend links to the patient before the code is emailed. Drawn with the Mobile
// app section's dialog parts, over the record.

const STATUSES = ['active', 'pending', 'invited', 'disabled'];
const IDLE = { status: 'idle', error: null, code: null, accountUuid: null, sent: null, linked: false };

// The record keeps one name; the invitation asks for two. Everything before the
// last word is the first name, which staff can correct before sending.
function nameParts(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return { firstName: words[0] || '', lastName: '' };
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
}

function statusLabel(status) {
  return STATUSES.includes(status) ? tx(`ui.mobile.status_${status}`) : status;
}

function untilDate(expiresAt) {
  return new Date(expiresAt).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function PatientAppLinkDialog({ patient, onClose, onLinked }) {
  const { permissions } = useXomicsAuth();
  // The directory and the link live in the Mobile app section; inviting does not.
  const mayFind = permissions.includes('PLATFORM_MOBILE_READ');
  const name = patient.name || tx('ui.patient.patient_n', { id: patient.id });
  const [mode, setMode] = useState(mayFind ? 'find' : 'invite');

  const [term, setTerm] = useState('');
  const [current, setCurrent] = useState([]);
  const [results, setResults] = useState({ status: 'idle', accounts: [] });
  const [chosen, setChosen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const [form, setForm] = useState(() => ({ ...nameParts(patient.name), email: typeof patient.email === 'string' ? patient.email : '' }));
  // linked: the invited account is known to belong to this patient by now.
  const [state, setState] = useState(IDLE);
  const set = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  // The accounts that belong to the patient already, whatever their status.
  useEffect(() => {
    if (!mayFind) return undefined;
    let live = true;
    loadMobileAccounts({ patient: patient.id, limit: 100 })
      .then((page) => { if (live) setCurrent(page.accounts); })
      .catch(() => { if (live) setCurrent([]); });
    return () => { live = false; };
  }, [mayFind, patient.id]);

  useEffect(() => {
    const query = term.trim();
    if (!mayFind || query === '') { setResults({ status: 'idle', accounts: [] }); return undefined; }
    let live = true;
    setResults((previous) => ({ ...previous, status: 'loading' }));
    const timer = window.setTimeout(async () => {
      try {
        const page = await loadMobileAccounts({ q: query, limit: 8 });
        if (live) setResults({ status: 'ready', accounts: page.accounts });
      } catch (caught) {
        if (live) setResults({ status: 'failed', accounts: [], error: errorMessage(caught) });
      }
    }, 260);
    return () => { live = false; window.clearTimeout(timer); };
  }, [mayFind, term]);

  // From an invitation that met an existing account: look that account up here.
  function findByEmail() {
    setState(IDLE);
    setChosen(null);
    setLinkError(null);
    setTerm(form.email.trim());
    setMode('find');
  }

  async function link() {
    setSaving(true);
    setLinkError(null);
    try {
      await linkMobileAccountPatient(chosen.uuid, patient.id);
      onLinked();
      onClose();
    } catch (caught) {
      setLinkError(errorMessage(caught));
      setSaving(false);
    }
  }

  function failed(caught, linked) {
    setState({
      status: 'idle', error: errorMessage(caught), code: caught.code || null, accountUuid: caught.body?.accountUuid || null, sent: null, linked,
    });
  }

  async function invite(event) {
    event.preventDefault();
    setState({ ...IDLE, status: 'sending' });
    try {
      const sent = await inviteMobileUser({
        email: form.email.trim(), firstName: form.firstName.trim(), lastName: form.lastName.trim(), patientId: patient.id,
      });
      setState({ ...IDLE, status: 'sent', accountUuid: sent.accountUuid, sent, linked: true });
      onLinked();
    } catch (caught) {
      // The backend links the account before it emails, so a failed email means linked.
      failed(caught, caught.code === 'INVITATION_EMAIL_FAILED');
    }
  }

  // A fresh code for an invitation that already exists; the earlier one stops working.
  async function resend() {
    const { linked } = state;
    setState((previous) => ({ ...previous, status: 'sending' }));
    try {
      const sent = await resendMobileInvitation(form.email.trim());
      setState({ ...IDLE, status: 'sent', sent, linked });
      onLinked();
    } catch (caught) {
      failed(caught, linked);
    }
  }

  // The invitation was made but not linked: link that account, then send its code.
  async function linkAndResend() {
    const { accountUuid } = state;
    setState((previous) => ({ ...previous, status: 'sending' }));
    try {
      await linkMobileAccountPatient(accountUuid, patient.id);
    } catch (caught) {
      setState((previous) => ({ ...previous, status: 'idle', error: errorMessage(caught) }));
      return;
    }
    onLinked();
    try {
      const sent = await resendMobileInvitation(form.email.trim());
      setState({ ...IDLE, status: 'sent', accountUuid, sent, linked: true });
    } catch (caught) {
      failed(caught, true);
    }
  }

  const inviteErrors = {
    EMAIL_TAKEN: tx('ui.patient.link_email_taken'),
    ALREADY_INVITED: tx('ui.patient.link_already_invited'),
    INVITATION_LINK_FAILED: tx('ui.patient.link_failed'),
  };
  const ways = [
    state.code === 'INVITATION_LINK_FAILED' && state.accountUuid && mayFind
      && <button type="button" key="retry" className="xo-link" onClick={linkAndResend}>{tx('ui.patient.link_retry')}</button>,
    (state.linked || state.code === 'ALREADY_INVITED')
      && <button type="button" key="resend" className="xo-link" onClick={resend}>{tx('ui.mobile.send_new_code')}</button>,
    ['EMAIL_TAKEN', 'ALREADY_INVITED'].includes(state.code) && mayFind
      && <button type="button" key="find" className="xo-link" onClick={findByEmail}>{tx('ui.patient.link_find')}</button>,
  ].filter(Boolean);
  const ready = form.firstName.trim() && form.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const searching = term.trim() !== '';
  const shown = searching ? results.accounts : current;
  // Whose account it is, in words; this patient's own say their status there, as the pill says Current.
  const belongsTo = (account) => {
    if (account.patientId === null) return tx('ui.mobile.not_linked');
    if (account.patientId === patient.id) return account.status === 'active' ? null : statusLabel(account.status);
    return tx('ui.patient.link_belongs_to', { name: account.patient?.name || tx('ui.mobile.patient_number', { id: account.patientId }) });
  };
  const moving = chosen !== null && chosen.patientId !== null && chosen.patientId !== patient.id;
  const tabs = mayFind && (
    <WorkspaceTabs value={mode} label={tx('ui.patient.link_choices')} className="is-compact"
      onChange={(next) => { setMode(next); setLinkError(null); }} tabs={[
        { value: 'find', label: tx('ui.patient.link_find'), Icon: Search },
        { value: 'invite', label: tx('ui.mobile.invite_title'), Icon: UserPlus },
      ]} />
  );

  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide" role="dialog" aria-modal="true" aria-labelledby="xo-rec-link-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Link2 aria-hidden="true" /></span>
          <div><h2 id="xo-rec-link-title">{tx('ui.patient.link_app_title')}</h2><p>{tx('ui.patient.link_app_detail', { name })}</p></div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        {state.status === 'sent' ? (
          <div className="xo-mob-dialog-done">
            <CheckCircle2 aria-hidden="true" />
            <strong>{tx('ui.mobile.invite_sent', { email: state.sent.email || form.email.trim() })}</strong>
            <p>{state.linked
              ? tx('ui.patient.link_invite_sent_detail', { name: state.sent.patient?.name || name, date: untilDate(state.sent.expiresAt) })
              : tx('ui.mobile.invite_sent_detail', { date: untilDate(state.sent.expiresAt) })}</p>
            <div className="xo-mob-dialog-actions">
              {!state.linked && mayFind && (
                <button type="button" className="xo-btn-2" onClick={findByEmail}><Search aria-hidden="true" />{tx('ui.patient.link_find')}</button>
              )}
              <button type="button" className="xo-btn" onClick={onClose}>{tx('ui.mobile.done')}</button>
            </div>
          </div>
        ) : mode === 'find' ? (
          <div className="xo-mob-form">
            {tabs}
            <label className="xo-mob-search-input">
              <Search aria-hidden="true" />
              <input value={term} onChange={(event) => { setTerm(event.target.value); setChosen(null); }}
                placeholder={tx('ui.mobile.search_accounts')} aria-label={tx('ui.patient.link_find')} autoFocus />
            </label>
            <div className="xo-mob-patient-results">
              {!searching && current.length > 0 && <small className="is-muted">{tx('ui.mobile.currently_linked')}</small>}
              {!searching && current.length === 0 && <small className="is-muted">{tx('ui.patient.link_find_hint')}</small>}
              {searching && results.status === 'loading' && <small className="is-muted">{tx('ui.mobile.searching')}</small>}
              {searching && results.status === 'failed' && <p className="xo-alert t-warn">{results.error}</p>}
              {searching && results.status === 'ready' && results.accounts.length === 0 && <small className="is-muted">{tx('ui.mobile.no_accounts_match')}</small>}
              {shown.map((account) => (
                <button type="button" key={account.uuid} className={chosen?.uuid === account.uuid ? 'is-chosen' : ''} onClick={() => setChosen(account)}>
                  <span className="xo-mob-avatar is-small">{initials(account.name, account.email)}</span>
                  <span><strong>{account.name || account.email}</strong>
                    <small>{[account.name ? account.email : null, belongsTo(account)].filter(Boolean).join(' · ')}</small></span>
                  {account.patientId === patient.id
                    ? <Pill tone="info">{tx('ui.mobile.current')}</Pill>
                    : account.status !== 'active' && <Pill>{statusLabel(account.status)}</Pill>}
                </button>
              ))}
            </div>
            {moving && (
              <p className="xo-alert t-warn">
                {tx('ui.patient.link_moves', { name: chosen.patient?.name || tx('ui.mobile.patient_number', { id: chosen.patientId }) })}
              </p>
            )}
            {linkError && <p className="xo-alert t-warn" role="alert">{linkError}</p>}
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.cancel')}</button>
              <button type="button" className="xo-btn" disabled={!chosen || saving || chosen.patientId === patient.id} onClick={link}>
                <Link2 aria-hidden="true" />{chosen ? tx('ui.mobile.link_to', { name: chosen.email || chosen.name }) : tx('ui.patient.link_choose_account')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={invite} className="xo-mob-form">
            {tabs}
            <div className="xo-mob-form-row">
              <label><span>{tx('ui.mobile.first_name')}</span><input value={form.firstName} onChange={set('firstName')} autoComplete="off" maxLength={255} required /></label>
              <label><span>{tx('ui.mobile.last_name')}</span><input value={form.lastName} onChange={set('lastName')} autoComplete="off" maxLength={255} required /></label>
            </div>
            <label><span>{tx('ui.mobile.email')}</span><input type="email" value={form.email} onChange={set('email')} autoComplete="off" maxLength={254} required /></label>
            <div className="xo-mob-invite-preview" aria-hidden="true">
              <Mail /><div><strong>{tx('ui.mobile.invite_preview_title')}</strong><small>{tx('ui.patient.link_invite_detail', { name })}</small></div>
            </div>
            {state.error && (
              <p className="xo-alert t-warn xo-rec-link-alert" role="alert">
                {inviteErrors[state.code] || state.error}
                {ways.length > 0 && <span>{ways}</span>}
              </p>
            )}
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.cancel')}</button>
              <button type="submit" className="xo-btn" disabled={!ready || state.status === 'sending'}>
                <Send aria-hidden="true" />{state.status === 'sending' ? tx('ui.mobile.sending') : tx('ui.mobile.send_invitation')}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
